import React, { useState, useEffect, useCallback } from 'react';
import useProfile from '../hooks/useProfile';
import ProfileInfoTab from '../components/profile/ProfileInfoTab';
import LLMConfigTab from '../components/profile/LLMConfigTab';
import APIKeysTab from '../components/profile/APIKeysTab';

const TABS = [
  { id: 'profile', label: 'Profile Info', icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )},
  { id: 'llm', label: 'LLM Configuration', icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
      <rect x="9" y="9" width="6" height="6" />
      <line x1="9" y1="1" x2="9" y2="4" />
      <line x1="15" y1="1" x2="15" y2="4" />
      <line x1="9" y1="20" x2="9" y2="23" />
      <line x1="15" y1="20" x2="15" y2="23" />
      <line x1="20" y1="9" x2="23" y2="9" />
      <line x1="20" y1="14" x2="23" y2="14" />
      <line x1="1" y1="9" x2="4" y2="9" />
      <line x1="1" y1="14" x2="4" y2="14" />
    </svg>
  )},
  { id: 'api', label: 'API Keys', icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  )},
];

function SettingsPage() {
  const [activeTab, setActiveTab] = useState('profile');
  const [toast, setToast] = useState(null);

  const {
    profile,
    loading,
    error,
    saving,
    updateProfile,
    uploadTeamLogo,
    testLLMConnection,
    testStatsBombConnection,
  } = useProfile();

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const handleProfileSave = useCallback(async (formData, logoFile) => {
    if (logoFile) {
      const logoResult = await uploadTeamLogo(logoFile);
      if (!logoResult.success) {
        showToast('error', `Logo upload failed: ${logoResult.error}`);
        return;
      }
    }

    const result = await updateProfile(formData);
    if (result.success) {
      showToast('success', 'Profile saved successfully.');
    } else {
      showToast('error', `Save failed: ${result.error}`);
    }
  }, [updateProfile, uploadTeamLogo, showToast]);

  const handleLLMSave = useCallback(async (updates) => {
    const result = await updateProfile(updates);
    if (result.success) {
      showToast('success', 'LLM configuration saved.');
    } else {
      showToast('error', `Save failed: ${result.error}`);
    }
  }, [updateProfile, showToast]);

  const handleAPISave = useCallback(async (updates) => {
    const result = await updateProfile(updates);
    if (result.success) {
      showToast('success', 'API credentials saved.');
    } else {
      showToast('error', `Save failed: ${result.error}`);
    }
  }, [updateProfile, showToast]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0a1628',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #1e3a5f',
            borderTopColor: '#f59e0b',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 16px',
          }} />
          <p style={{ color: '#64748b', fontSize: '14px' }}>Loading your profile...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0a1628',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          background: '#0f2040',
          border: '1px solid #ef4444',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '440px',
          textAlign: 'center',
        }}>
          <div style={{ color: '#ef4444', fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>
            Failed to load profile
          </div>
          <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '16px' }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: '#f59e0b',
              color: '#0a1628',
              fontWeight: '600',
              padding: '10px 24px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0a1628' }}>
      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: '72px',
            right: '24px',
            zIndex: 9999,
            padding: '12px 20px',
            borderRadius: '10px',
            background: toast.type === 'success' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
            border: `1px solid ${toast.type === 'success' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            color: toast.type === 'success' ? '#22c55e' : '#ef4444',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            maxWidth: '400px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            animation: 'slideIn 0.3s ease-out',
          }}
        >
          {toast.type === 'success' ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          )}
          {toast.message}
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      <div style={{ maxWidth: '960px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Page Header */}
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ color: '#f1f5f9', fontSize: '28px', fontWeight: '800', margin: '0 0 4px 0' }}>
            Profile Settings
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
            Manage your coaching profile, AI engine configuration, and data integrations.
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '24px',
          background: '#0f2040',
          borderRadius: '10px',
          padding: '4px',
          border: '1px solid #1e3a5f',
        }}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: isActive ? '#f59e0b' : 'transparent',
                  color: isActive ? '#0a1628' : '#64748b',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'profile' && (
            <ProfileInfoTab
              profile={profile}
              onSave={handleProfileSave}
              saving={saving}
            />
          )}
          {activeTab === 'llm' && (
            <LLMConfigTab
              profile={profile}
              onSave={handleLLMSave}
              saving={saving}
              testLLMConnection={testLLMConnection}
            />
          )}
          {activeTab === 'api' && (
            <APIKeysTab
              profile={profile}
              onSave={handleAPISave}
              saving={saving}
              testStatsBombConnection={testStatsBombConnection}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
